import { API_URL } from "General/APIEndpoints";
import { action, observable, runInAction, makeObservable, computed } from "mobx";
import { handleStoreState } from "Stores/RESTStore/CoreStore";
import { API } from "General/API";
import { CoreStore } from "Stores/RESTStore/CoreStore";
import { notificationError, notificationSuccess } from "utils/libHelpers/AntdNotification";
import i18n, { I18N_NS } from "Config/i18n";

export type MetadataViewMode = "ADMIN" | "SETTINGS";
class MetadataStore extends CoreStore {
  data: API_Metadata[] = [];
  projectId: string | number = null;
  mode: MetadataViewMode = "ADMIN";

  constructor() {
    super();

    makeObservable(this, {
      data: observable,
      mode: observable,
      projectId: observable,
      clear: action.bound,
      setMode: action.bound,
      getMetaById: action.bound,
      setProjectId: action.bound,
      fetch: action.bound,
      edit: action.bound,
      save: action.bound,
      activeElemetsInFormatW: computed,
    });
  }

  clear() {
    this.data = [];
    this.projectId = null;
    this.mode = "ADMIN";
  }

  url(mode: MetadataViewMode) {
    return mode === "ADMIN"
      ? API_URL.ADMIN_PROJECTS.getProjectMetadataList(this.projectId)
      : API_URL.METADATA.getDefinitionsFull(this.projectId);
  }

  processResponse = (data: API_Metadata[], mode: MetadataViewMode) => {
    if (!data) {
      return [];
    }
    return mode === "ADMIN" ? data : data.filter((md) => md.active && md.format.toLowerCase() === "w");
  };

  get activeElemetsInFormatW() {
    return this.data.filter((md) => md.active && md.format.toLowerCase() === "w");
  }

  @handleStoreState()
  async fetch(id?: number) {
    if (id) {
      this.setProjectId(id);
    }
    try {
      const response: API_Metadata[] = await API.getData(this.url(this.mode));
      if (response) {
        runInAction(() => {
          this.data = this.processResponse(response, this.mode);
        });
      }
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState()
  async edit(formData: API_Metadata) {
    try {
      let response;
      this.mode === "ADMIN"
        ? (response = await API.editData<string>(
            API_URL.ADMIN_PROJECTS.updateProjectMetadata(this.projectId, formData.definitionId),
            formData
          ))
        : (response = await API.editData<string>(
            API_URL.METADATA.updateProjectMetadata_1(this.projectId, formData.definitionId),
            formData
          ));
      const oldData = this.data.slice();
      const index = oldData.findIndex((item) => item.definitionId === formData.definitionId);
      if (index !== -1) {
        oldData[index] = formData;
      }
      runInAction(() => {
        this.data = oldData;
      });
      if (response.includes("Warning") || response.includes("Warnung")) {
        notificationError(i18n.t("Error", { ns: I18N_NS.PROJECT }), `${response}`);
      } else {
        notificationSuccess(
          i18n.t("Success", { ns: I18N_NS.PROJECT }),
          i18n.t("Metadata updated successfully", { ns: I18N_NS.ADMIN })
        );
      }
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async save(formData: API_Metadata) {
    try {
      const response = await API.newData(API_URL.ADMIN_PROJECTS.createProjectMetadata(this.projectId), formData);
      if (response) {
        runInAction(() => {
          this.data = this.data.concat(response);
        });
      }
    } catch (error) {
      throw error;
    }
  }

  getMetaById(id: number) {
    const data = this.data.find((item) => item.definitionId === id);
    return data;
  }

  setProjectId(id: string | number) {
    this.projectId = id;
  }

  setMode(mode: MetadataViewMode) {
    this.mode = mode || "ADMIN";
  }
}

export const metadataStore = new MetadataStore();
